import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw, Home, Loader2 } from "lucide-react";
import { isNativeRuntime } from "@/lib/nativeRuntime";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  isModuleError: boolean;
  isRecovering: boolean;
}

/**
 * Session-scoped reload flag. Prevents infinite reload loops:
 * - First time a module error reaches ErrorBoundary → silent reload
 * - After reload, if it fails again → show error UI (no more reloads)
 */
const MODULE_RELOAD_KEY = "__gistgift_eb_module_reload";

function isModuleLoadError(error?: Error): boolean {
  if (!error) return false;
  const msg = error.message?.toLowerCase() ?? "";
  return (
    msg.includes("importing a module") ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("loading chunk") ||
    msg.includes("dynamically imported module") ||
    msg.includes("loading css chunk")
  );
}

async function clearCachesAndReload(targetHash = ""): Promise<void> {
  try {
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
  } catch (err) {
    console.warn("[ErrorBoundary] Failed to clear caches:", err);
  }
  const baseUrl = `${window.location.origin}/?_cb=${Date.now()}`;
  window.location.replace(targetHash ? `${baseUrl}${targetHash}` : baseUrl);
}

/**
 * Check if we already did a silent reload for module errors this session.
 * Uses sessionStorage so it resets when the app is force-closed.
 */
function hasAlreadyReloadedForModuleError(): boolean {
  try {
    return sessionStorage.getItem(MODULE_RELOAD_KEY) === "1";
  } catch {
    return false;
  }
}

function markModuleReloadAttempted(): void {
  try {
    sessionStorage.setItem(MODULE_RELOAD_KEY, "1");
  } catch {
    // Ignore
  }
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isModuleError: false,
      isRecovering: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const moduleError = isModuleLoadError(error);
    const native = isNativeRuntime();

    // Single silent reload for module errors (web + native).
    // The sessionStorage flag prevents infinite loops — second failure shows error UI.
    if (moduleError && !hasAlreadyReloadedForModuleError()) {
      console.log("[ErrorBoundary] Module error detected on web, will attempt silent reload");
      return {
        hasError: true,
        error,
        isModuleError: true,
        isRecovering: true,
      };
    }

    return {
      hasError: true,
      error,
      isModuleError: moduleError,
      isRecovering: false,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[ErrorBoundary] Error details:", {
      error: error.message,
      isModuleError: this.state.isModuleError,
      componentStack: errorInfo.componentStack?.slice(0, 500),
    });

    this.setState({ errorInfo });

    // Silent reload for module errors (both web and native).
    if (this.state.isRecovering && this.state.isModuleError) {
      markModuleReloadAttempted();
      console.log("[ErrorBoundary] Performing silent reload for module error (web)");
      setTimeout(() => clearCachesAndReload(), 500);
    }
  }

  handleRestart = (): void => {
    // Clear all recovery flags so next boot is clean
    try {
      sessionStorage.removeItem(MODULE_RELOAD_KEY);
      sessionStorage.removeItem("__gistgift_chunk_reload");
    } catch {}
    window.location.href = "/";
  };

  handleHardReload = (): void => {
    try {
      sessionStorage.removeItem(MODULE_RELOAD_KEY);
      sessionStorage.removeItem("__gistgift_chunk_reload");
    } catch {}

    const nativeHashTarget = isNativeRuntime() ? "#/boot" : "";
    clearCachesAndReload(nativeHashTarget);
  };

  handleGoHome = (): void => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      isModuleError: false,
      isRecovering: false,
    });
    window.location.href = "/#/home";
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, isModuleError, isRecovering } = this.state;
    const isNative = isNativeRuntime();
    const isNetworkIssue = isModuleError && isNative;
    const showUpdateUI = isModuleError && !isNative;

    // Show brief spinner while doing silent reload
    if (isRecovering) {
      return (
        <div
          className="min-h-screen flex items-center justify-center p-6 bg-background"
          style={{
            paddingTop: "calc(1.5rem + var(--safe-area-top, 0px))",
            paddingBottom: "calc(1.5rem + var(--safe-area-bottom, 0px))",
          }}
        >
          <div className="max-w-sm w-full space-y-6 text-center">
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin" />
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-foreground">
                {isNative ? "Reconnecting…" : "Updating…"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isNative
                  ? "Trying to reconnect to GistGift"
                  : "Loading latest version of GistGift"}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Error UI — shown only after silent reload already failed
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-background to-destructive/5"
        style={{
          paddingTop: "calc(1.5rem + var(--safe-area-top, 0px))",
          paddingBottom: "calc(1.5rem + var(--safe-area-bottom, 0px))",
        }}
      >
        <div className="max-w-sm w-full space-y-6">
          <div className="flex justify-center animate-in zoom-in duration-500">
            <div className="rounded-full bg-destructive/10 p-4">
              <AlertCircle className="w-12 h-12 text-destructive" />
            </div>
          </div>

          <div className="space-y-3 text-center animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
            <h1 className="text-2xl font-bold text-foreground">
              {isNetworkIssue
                ? "Connection Issue"
                : showUpdateUI
                  ? "Update Available"
                  : "Something Went Wrong"}
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {isNetworkIssue
                ? "GistGift couldn't load some resources. Please check your internet connection and try again."
                : showUpdateUI
                  ? "A new version of GistGift is available. Please reload to get the latest features and fixes."
                  : "We encountered an unexpected issue. Your data is safe, and we can help you get back on track."}
            </p>
          </div>

          {!isModuleError && error && (
            <details className="bg-muted/50 rounded-lg p-4 text-left animate-in fade-in duration-500 delay-200">
              <summary className="text-xs font-medium text-muted-foreground cursor-pointer">
                Technical Details
              </summary>
              <pre className="mt-2 text-xs font-mono text-muted-foreground whitespace-pre-wrap break-all">
                {error.message}
                {this.state.errorInfo && (
                  <>
                    {"\n\n"}
                    {this.state.errorInfo.componentStack}
                  </>
                )}
              </pre>
            </details>
          )}

          <div className="space-y-3 animate-in fade-in slide-in-from-bottom-6 duration-500 delay-300">
            <Button onClick={this.handleHardReload} className="w-full" size="lg">
              {isModuleError && <RefreshCw className="w-4 h-4 mr-2" />}
              {isNetworkIssue ? "Try Again" : showUpdateUI ? "Update Now" : "Restart App"}
            </Button>

            {!isModuleError && (
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={this.handleGoHome} variant="outline" size="lg">
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </Button>
                <Button onClick={this.handleHardReload} variant="outline" size="lg">
                  Clear Cache
                </Button>
              </div>
            )}
          </div>

          <p className="text-xs text-center text-muted-foreground animate-in fade-in duration-700 delay-500">
            If this persists, try clearing your browser cache or reinstalling the app.
          </p>
        </div>
      </div>
    );
  }
}
